import { ALERT_PARTS } from './alertParts';
import type { AlertColorName, AlertStyleConfig, AlertStatus } from './types';

export const ALERT_STATUS_TO_SURFACE_COLOR_MAP: Record<
  AlertStatus,
  AlertColorName
> = {
  error: 'surfaceError',
  warning: 'surfaceWarning',
  success: 'surfaceSuccess',
  info: 'surfaceInfo',
  assistance: 'surfaceAssistance',
};

const isAlertStatus = (value: unknown): value is AlertStatus =>
  typeof value === 'string' &&
  Object.keys(ALERT_STATUS_TO_SURFACE_COLOR_MAP).includes(value);

const getAlertColor = ({
  alertColorName,
}: {
  alertColorName: AlertColorName;
}) => `alert.${alertColorName}`;

const getAlertColors = ({
  status,
}: {
  status: unknown;
}): { color: AlertColorName; surfaceColor: AlertColorName } | null =>
  isAlertStatus(status)
    ? {
        color: getAlertColor({ alertColorName: status }),
        surfaceColor: getAlertColor({
          alertColorName: ALERT_STATUS_TO_SURFACE_COLOR_MAP[status],
        }),
      }
    : null;

const baseStyle: AlertStyleConfig['baseStyle'] = ({ status }) => {
  const alertColors = getAlertColors({ status });
  return {
    container: {
      backgroundColor: alertColors?.surfaceColor,
      color: alertColors?.color,
      alignItems: 'flex-start',
      width: '100%',
      display: 'flex',
      position: 'relative',
      overflow: 'hidden',
    },
    title: {
      color: 'feature.primary',
      fontWeight: 'heavy',
      marginEnd: 1,
    },
    description: {
      display: 'inline',
      color: 'feature.primary',
      fontWeight: 'default',
    },
    icon: { flexShrink: 0, color: 'inherit' },
  };
};

const SIZES = {
  md: {
    container: {
      py: 'calc(0.75rem - 1px)',
      paddingLeft: '0.75rem',
      paddingRight: 'calc(1rem - 1px)',
      fontSize: 'md',
      lineHeight: 'md',
    },
    icon: {
      marginEnd: 1.5,
      w: '1.375rem',
      h: 3,
    },
  },
  sm: {
    container: {
      py: 'calc(0.5rem - 1px)',
      paddingLeft: '0.5rem',
      paddingRight: 'calc(0.75rem - 1px)',
      fontSize: 'sm',
      lineHeight: 'sm',
    },
    icon: {
      marginEnd: 1,
      w: '1.125rem',
      h: 2.5,
    },
  },
};

const VARIANTS: AlertStyleConfig['variants'] = {
  subtle: ({ status }) => ({
    container: {
      border: '1px',
      borderColor: getAlertColors({ status })?.color,
      borderRadius: 'md',
    },
  }),
  borderless: () => ({}),
};

const DEFAULT_PROPS = {
  size: 'md',
  variant: 'subtle',
  status: 'info',
} as const;

export const ALERT_STYLE_CONFIG: AlertStyleConfig = {
  baseStyle,
  sizes: SIZES,
  variants: VARIANTS,
  defaultProps: DEFAULT_PROPS,
  parts: ALERT_PARTS,
};
